import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Box, 
  CircularProgress, 
  Alert, 
  Button,
  IconButton,
  Chip,
  Divider,
  Tooltip,
  Badge,
  Paper
} from '@mui/material';
import { 
  FilterAlt as FilterIcon, 
  Diamond as DiamondIcon,
  ViewAgenda as ViewAgendaIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  AutoAwesome as AutoAwesomeIcon,
  FilterAltOff as FilterAltOffIcon
} from '@mui/icons-material';
import AmslerGridCard from '../components/AmslerGridCard';
import AmslerGridDialog from '../components/AmslerGridDialog';
import FilterDialog from '../components/FilterDialog';
import SubscriptionModal from '../components/SubscriptionModal';
import Pagination from '../components/Pagination';
import EmptyState from '../components/EmptyState';
import { DeleteConfirmationDialog } from '../components';
import { AmslerGridData } from '../types/AmslerGridData';
import { useFirebaseService } from '../contexts/FirebaseServiceContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import FullTestResultDialog from '../components/FullTestResultDialog';
import { secureLogger } from '../utils/loggerUtils';

// Import FilterOptions type from FilterDialog component
interface FilterOptions {
  eyeType: 'all' | 'left' | 'right';
  dateFrom: string | null;
  dateTo: string | null;
  month: number | null;
  year: number | null;
}

// Initial filter state
const initialFilters: FilterOptions = {
  eyeType: 'all',
  dateFrom: null,
  dateTo: null,
  month: null,
  year: null
};

// --- Hybrid Auto-Refresh Hooks ---
function useUserActivity(timeoutMs: number) {
  const [active, setActive] = useState(true);
  const timer = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const handleActivity = () => {
      setActive(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setActive(false), timeoutMs);
    };
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('touchstart', handleActivity);
    handleActivity();
    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      clearTimeout(timer.current);
    };
  }, [timeoutMs]);
  return active;
}

function useAutoRefresh(isPremium: boolean, onRefresh: () => void) {
  const refreshInterval = isPremium ? 5 * 60 * 1000 : 10 * 60 * 1000;
  const [showDialog, setShowDialog] = useState(false);
  const userActive = useUserActivity(refreshInterval);

  useEffect(() => {
    if (userActive) {
      setShowDialog(false);
      const interval = setInterval(() => {
        onRefresh();
      }, refreshInterval);
      return () => clearInterval(interval);
    } else {
      setShowDialog(true);
    }
  }, [userActive, refreshInterval, onRefresh]);

  return showDialog;
}
// --- End Hybrid Auto-Refresh Hooks ---

const Dashboard: React.FC = () => {
  // State for pagination and filtering
  const [page, setPage] = useState(0);
  const [totalGrids, setTotalGrids] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [lastRefresh, setLastRefresh] = useState<number>(Date.now());
  
  // State for UI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [grids, setGrids] = useState<AmslerGridData[]>([]);
  const [selectedGrid, setSelectedGrid] = useState<AmslerGridData | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  
  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [gridToDelete, setGridToDelete] = useState<string | undefined>(undefined);
  
  // Add state for the test result dialog
  const [selectedTestId, setSelectedTestId] = useState<string | null>(null);
  const [testResultDialogOpen, setTestResultDialogOpen] = useState(false);
  
  // Service contexts
  const { firebaseService } = useFirebaseService();
  const { isPremium } = useSubscription();
  const navigate = useNavigate();
  
  // Constants
  const LIMIT = 9; // Items per page
  
  // Check if any filters are active
  const hasActiveFilters = filters.eyeType !== 'all' || 
    filters.dateFrom !== null || 
    filters.dateTo !== null || 
    filters.month !== null || 
    filters.year !== null;
  
  // Load grids based on pagination and filters
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        secureLogger.log(`Fetching grid data - Page ${page}, Filters: ${JSON.stringify(filters)}`);
        
        // IMPORTANT: First check if the database is completely empty
        // This needs to happen before any errors can be thrown
        secureLogger.log("Checking if database is empty before attempting fetch...");
        let isEmpty = false;
        try {
          isEmpty = await firebaseService.isDatabaseEmpty();
        } catch (emptyCheckError) {
          secureLogger.error("Error checking if database is empty:", emptyCheckError);
          // If we can't check, it's better to assume it's empty for a better UX
          isEmpty = true;
        }
        
        if (isEmpty) {
          secureLogger.log("Database is empty - showing empty state directly");
          setGrids([]);
          setTotalGrids(0);
          setHasMore(false);
          setLoading(false);
          // IMPORTANT: Clear any potential error state
          setError(null);
          return;
        }
        
        secureLogger.log("Database is not empty, proceeding with normal fetch");
        
        // On initial load with no filters, check for legacy data to migrate and clean up
        if (page === 0 && !hasActiveFilters) {
          try {
            // First check if there's legacy data to migrate
            const migrationPerformed = await firebaseService.checkAndMigrateOldData();
            
            if (migrationPerformed) {
              secureLogger.log("Migration from legacy data completed");
            }
            
            // Always clean up legacy paths after migration attempt
            await firebaseService.cleanupLegacyData();
            
            // Then clean up any invalid data in main storage
            await firebaseService.cleanupInvalidGridData();
            secureLogger.log("Completed initial data cleanup");
          } catch (cleanupErr) {
            secureLogger.error("Error in initial data handling:", cleanupErr);
            // Continue with fetching even if cleanup fails
          }
        }
        
        // Apply date filters
        let dateFrom: number | undefined;
        let dateTo: number | undefined;
        
        if (filters.dateFrom) {
          dateFrom = new Date(filters.dateFrom).getTime();
        } else if (filters.month !== null && filters.year !== null) {
          // If month/year are set but no specific date
          dateFrom = new Date(filters.year, filters.month, 1).getTime();
        }
        
        if (filters.dateTo) {
          dateTo = new Date(filters.dateTo).getTime();
        } else if (filters.month !== null && filters.year !== null) {
          // Last day of the selected month
          const lastDay = new Date(filters.year, filters.month + 1, 0).getDate();
          dateTo = new Date(filters.year, filters.month, lastDay, 23, 59, 59).getTime();
        }
        
        // Fetch grids with pagination and filters
        const result = await firebaseService.getUserGrids({
          page,
          limit: LIMIT,
          dateFrom,
          dateTo,
          eyeType: filters.eyeType !== 'all' ? filters.eyeType : undefined,
          isPremium
        });
        
        secureLogger.log(`Fetched ${result.grids.length} grids, total: ${result.total}, hasMore: ${result.hasMore}`);
        
        // Ensure we only process valid grid data
        const validGrids = result.grids.filter(grid => 
          grid && 
          grid.id && 
          typeof grid.id === 'string' && 
          grid.id.trim() !== '' &&
          typeof grid.timestamp === 'number'
        );
        
        if (validGrids.length !== result.grids.length) {
          secureLogger.warn(`Filtered out ${result.grids.length - validGrids.length} invalid grid entries`);
        }
        
        // If we have unexpected dummy data (empty valid grids but non-zero total), force cleanup
        if (validGrids.length === 0 && result.total > 0) {
          secureLogger.warn("Inconsistent data detected, forcing cleanup...");
          try {
            await firebaseService.cleanupInvalidGridData();
            // Try fetching again after cleanup
            const refreshResult = await firebaseService.getUserGrids({
              page: 0, // Reset to first page
              limit: LIMIT,
              isPremium
            });
            
            setGrids(refreshResult.grids.filter(g => 
              g && g.id && typeof g.timestamp === 'number'
            ));
            setTotalGrids(refreshResult.total);
            setHasMore(refreshResult.hasMore);
          } catch (cleanupErr) {
            secureLogger.error("Error in forced cleanup:", cleanupErr);
            // Use empty arrays if cleanup fails
            setGrids([]);
            setTotalGrids(0);
            setHasMore(false);
          }
        } else {
          // Normal case - use the filtered data
          setGrids(validGrids);
        setTotalGrids(result.total);
        setHasMore(result.hasMore);
        }
        
        setLoading(false);
      } catch (err) {
        secureLogger.error('Error fetching Amsler Grid data:', err);
        
        // ALWAYS check for empty database first, regardless of error
        try {
          secureLogger.log("Checking if database is empty after error...");
          const emptyCheck = await firebaseService.isDatabaseEmpty();
          
          if (emptyCheck) {
            secureLogger.log("Database confirmed empty after error - showing empty state UI");
            // Zero out all data and clear error state to show empty UI
            setGrids([]);
            setTotalGrids(0);
            setHasMore(false);
            setError(null); // Clear error so empty state UI is shown
            setLoading(false);
            return;
          }
          
          secureLogger.log("Database not empty after error check - proceeding with error handling");
        } catch (emptyCheckErr) {
          secureLogger.error("Error checking if database is empty:", emptyCheckErr);
          // Even if there's an error in the empty check, assume it COULD be empty
          // This makes the UX better in case of database connection issues
          secureLogger.log("Assuming database might be empty due to check error");
          setGrids([]);
          setTotalGrids(0);
          setHasMore(false);
          setError(null); // Clear error to show empty state UI instead
          setLoading(false);
          return;
        }
        
        // Only show errors if we're SURE the database isn't empty
        if (err instanceof Error) {
          const errorMessage = err.message;
          secureLogger.log(`Processing error message: "${errorMessage}"`);
          
          if (errorMessage.includes('not found') || 
              errorMessage.includes('permission-denied') || 
              errorMessage.includes('no data available') ||
              errorMessage.includes('empty')) {
            // One more failsafe - if the error message suggests empty data,
            // clear the error and show empty state UI
            secureLogger.log("Error message suggests empty data - showing empty state");
            setGrids([]);
            setTotalGrids(0);
            setHasMore(false);
            setError(null);
            setLoading(false);
            return;
          } else if (errorMessage.includes('network') || 
                    errorMessage.includes('connection') || 
                    errorMessage.includes('timeout')) {
            setError('Network error. Please check your internet connection and try again.');
          } else {
            setError('Failed to load your test history. Please refresh the page or try again later.');
          }
        } else {
          setError('Failed to load your test history. Please refresh the page or try again later.');
        }
        
        // Zero out grids for any error
        setGrids([]);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [firebaseService, page, filters, isPremium, hasActiveFilters, lastRefresh]);

  // Handle grid opening
  const handleGridOpen = (grid: AmslerGridData) => {
    // Instead of navigating, open the dialog
    if (grid && grid.id) {
      setSelectedTestId(grid.id);
      setTestResultDialogOpen(true);
    } else {
      secureLogger.error('Cannot open test result: Grid is missing ID');
    }
  };

  // Handle closing the test result dialog
  const handleCloseTestResultDialog = () => {
    setTestResultDialogOpen(false);
    setSelectedTestId(null);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    secureLogger.log('Closing AmslerGrid dialog');
    setDialogOpen(false);
    setSelectedGrid(null); // Clear selected grid when closing
  };

  // Handle filter application
  const handleApplyFilters = (newFilters: typeof filters) => {
    setFilters(newFilters);
    setPage(0); // Reset to first page when filters change
  };

  // Handle grid deletion - can accept either a grid ID or a full grid object
  const handleDeleteGrid = (gridIdOrGrid?: string | AmslerGridData) => {
    // Extract the ID from either a grid object or a string ID
    let gridId: string | undefined;
    let gridObject: AmslerGridData | undefined;
    
    if (typeof gridIdOrGrid === 'string') {
      gridId = gridIdOrGrid;
      // Find the corresponding grid object to display in confirmation
      gridObject = grids.find(g => g.id === gridId);
    } else if (gridIdOrGrid && typeof gridIdOrGrid === 'object' && gridIdOrGrid.id) {
      gridId = gridIdOrGrid.id;
      gridObject = gridIdOrGrid;
    }
    
    if (!gridId) {
      secureLogger.error('Cannot delete grid: No valid ID provided');
      return;
    }
    
    secureLogger.log(`Preparing to delete grid: ${gridId}`);
    
    // If we're in the dialog view, close it first
    if (dialogOpen) {
      setDialogOpen(false);
    }
    
    // Set the grid to delete and open confirmation
    setGridToDelete(gridId);
    
    // Small delay to ensure dialog is closed before showing confirmation
    setTimeout(() => {
      setDeleteConfirmOpen(true);
    }, 100);
  };

  // Confirm deletion
  const confirmDeleteGrid = async () => {
    if (!gridToDelete) return;
    
    try {
      setLoading(true);
      secureLogger.log(`Deleting grid ${gridToDelete} from Firebase`);
      
      // Check if we're deleting the last item
      const isLastItem = grids.length === 1;
      secureLogger.log(`Is this the last item? ${isLastItem ? 'Yes' : 'No'}`);
      
      // Delete from Firebase
      await firebaseService.deleteGrid(gridToDelete);
      
      // Close confirmation dialog
      setDeleteConfirmOpen(false);
      
      // Clear any grid being viewed
      setSelectedGrid(null);
      
      // If this is the last item, clean up all data to prevent dummy data
      if (isLastItem) {
        secureLogger.log('Deleted the last item, cleaning up any remaining data');
        try {
          // Clean up any invalid data that might be left
          await firebaseService.cleanupInvalidGridData();
          // Set state directly to empty to avoid refetching
          setGrids([]);
          setTotalGrids(0);
          setHasMore(false);
        } catch (cleanupErr) {
          secureLogger.error('Error during cleanup:', cleanupErr);
          // Still proceed with UI update even if cleanup fails
        }
        setLoading(false);
      } else {
        // Update UI to reflect the deletion
        setGrids(prevGrids => prevGrids.filter(g => g.id !== gridToDelete));
        setTotalGrids(prev => Math.max(0, prev - 1));
      
        // If we deleted the last item on the page and not on first page, go back
        if (grids.length === 1 && page > 0) {
          secureLogger.log('Last item on page deleted, going back to previous page');
          setPage(page - 1);
        } else {
          // Otherwise do a full refresh to ensure data consistency
          refreshCurrentPage();
        }
      }
      
      // Reset state
      setGridToDelete(undefined);
    } catch (err) {
      secureLogger.error('Error deleting grid:', err);
      if (err instanceof Error && err.message === 'Premium subscription required to delete tests') {
        setError('Premium subscription required to delete tests. Please upgrade to continue.');
        setSubscriptionModalOpen(true);
      } else {
        setError('Failed to delete the grid. Please try again.');
      }
      setLoading(false);
    }
  };

  // Add a function to force a complete cleanup
  const forceCleanupAndRefresh = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Run the cleanup to remove any invalid data
      await firebaseService.cleanupInvalidGridData();
      
      // Refresh the data
      refreshCurrentPage();
    } catch (err) {
      secureLogger.error('Error during force cleanup:', err);
      setError('Failed to clean up data. Please try again later.');
      setLoading(false);
    }
  };

  // Cancel deletion
  const cancelDeleteGrid = () => {
    secureLogger.log('Delete cancelled');
    setDeleteConfirmOpen(false);
    setGridToDelete(undefined);
  };

  // Handle grid sharing
  const handleShareGrid = (grid?: AmslerGridData) => {
    if (!grid) return;
    secureLogger.log(`Sharing grid: ${grid.id}`);
    alert('Sharing functionality would be implemented here');
  };

  // Handle grid download
  const handleDownloadGrid = (grid?: AmslerGridData) => {
    if (!grid) return;
    
    // Generate an image for download
    const imageUrl = firebaseService.generateGridImage(grid);
    
    // Create a download link
    const a = document.createElement('a');
    a.href = imageUrl;
    a.download = `amsler-grid-${new Date(grid.timestamp).toISOString().split('T')[0]}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalGrids / LIMIT);

  // Refresh the current page data
  const refreshCurrentPage = () => {
    secureLogger.log('Manual refresh requested');
    
    // Clear any errors before refresh
    setError(null);
    
    // Reset to first page if there was an error
    if (error) {
      setPage(0);
    }
    
    // Force a reload by triggering the useEffect
    setLoading(true);
    
    // Set a small timeout to ensure state updates before triggering fetch
    setTimeout(() => {
      // This causes the useEffect to re-run
      const refreshTime = Date.now();
      setLastRefresh(refreshTime);
    }, 100);
  };

  // Use the hybrid auto-refresh logic
  const showRefreshDialog = useAutoRefresh(isPremium, refreshCurrentPage);

  // Add a function to force delete all data
  const forceDeleteAllData = async () => {
    try {
      if (!window.confirm('WARNING: This will delete ALL of your grid test data. This cannot be undone. Continue?')) {
        return;
      }
      
      setLoading(true);
      setError(null);
      
      // Delete all grid data
      await firebaseService.deleteAllGrids();
      
      // Reset UI state
      setGrids([]);
      setTotalGrids(0);
      setHasMore(false);
      setPage(0);
      setError(null);
      setLoading(false);
      
      alert('All data has been deleted successfully.');
    } catch (err) {
      secureLogger.error('Error during force delete:', err);
      setError('Failed to delete all data. Please try again later.');
      setLoading(false);
    }
  };

  // Add a function to force cleanup of a specific path
  const forceCleanupSpecificPath = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Attempt to clean the specific problematic path
      const specificPath = 'users/PeqDZAZYKYS0gQ6rbQG4AvfuE0r1/AmslerGridImage';
      secureLogger.log(`Attempting to clean specific path: ${specificPath}`);
      
      // Call the new method
      const success = await firebaseService.cleanupSpecificPath(specificPath);
      
      if (success) {
        alert(`Successfully cleaned up path: ${specificPath}`);
      } else {
        alert(`Could not fully clean up path: ${specificPath}. Check console for details.`);
      }
      
      setLoading(false);
    } catch (err) {
      secureLogger.error('Error during force cleanup of specific path:', err);
      setError('Failed to clean up specific path. Check console for details.');
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header with actions */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2
      }}>
        <Typography variant="h4" component="h1">
          Amsler Grid
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Filter tests">
            <IconButton 
              onClick={() => setFilterDialogOpen(true)}
              sx={{ 
                bgcolor: hasActiveFilters ? 'rgba(187, 134, 252, 0.1)' : 'transparent',
                border: hasActiveFilters ? '1px solid #BB86FC' : 'none'
              }}
            >
              <FilterIcon 
                color={hasActiveFilters ? "primary" : "inherit"} 
              />
            </IconButton>
          </Tooltip>
          
          {!isPremium && (
            <Button 
              variant="outlined"
              color="primary"
              startIcon={<DiamondIcon />}
              onClick={() => setSubscriptionModalOpen(true)}
              sx={{ 
                borderColor: '#BB86FC',
                color: '#BB86FC',
                '&:hover': {
                  borderColor: '#9965F5',
                  backgroundColor: 'rgba(187, 134, 252, 0.08)'
                }
              }}
            >
              Upgrade
            </Button>
          )}
        </Box>
      </Box>
      
      {/* Active filters display */}
      {hasActiveFilters && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          mb: 2,
          py: 1,
          px: 2,
          bgcolor: '#1E1E1E',
          borderRadius: 1,
          flexWrap: 'wrap'
        }}>
          <Typography variant="body2" sx={{ color: '#A0A0A0' }}>
            Filters:
          </Typography>
          
          {filters.eyeType !== 'all' && (
            <Chip 
              label={`Eye: ${filters.eyeType === 'left' ? 'Left' : 'Right'}`}
              size="small"
              color="primary"
              variant="outlined"
              onDelete={() => handleApplyFilters({...filters, eyeType: 'all'})}
            />
          )}
          
          {filters.dateFrom && (
            <Chip 
              label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
              size="small"
              color="primary"
              variant="outlined"
              onDelete={() => handleApplyFilters({...filters, dateFrom: null})}
            />
          )}
          
          {filters.dateTo && (
            <Chip 
              label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
              size="small"
              color="primary"
              variant="outlined"
              onDelete={() => handleApplyFilters({...filters, dateTo: null})}
            />
          )}
          
          {filters.month !== null && filters.year !== null && (
            <Chip 
              label={`${new Date(filters.year, filters.month).toLocaleString('default', { month: 'long', year: 'numeric' })}`}
              size="small"
              color="primary"
              variant="outlined"
              onDelete={() => handleApplyFilters({...filters, month: null, year: null})}
            />
          )}
          
          <Button 
            size="small"
            onClick={() => handleApplyFilters(initialFilters)}
            sx={{ ml: 'auto', fontSize: '0.75rem' }}
          >
            Clear All
          </Button>
        </Box>
      )}
      
      {/* Premium restriction notice */}
      {!isPremium && totalGrids > LIMIT && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3, 
            backgroundColor: 'rgba(187, 134, 252, 0.1)', 
            color: '#E0E0E0',
            '& .MuiAlert-icon': {
              color: '#BB86FC'
            }
          }}
          icon={<DiamondIcon />}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">
              Showing {LIMIT} of {totalGrids} tests. Upgrade to Premium to access your full history.
            </Typography>
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => setSubscriptionModalOpen(true)}
              sx={{ ml: 2 }}
            >
              Upgrade
            </Button>
          </Box>
        </Alert>
      )}
      
      {/* 
        Enhanced rendering logic:
        1. Special case: If no grids but still loading, show loading spinner
        2. Special case: If no grids, show empty state REGARDLESS of error (error will be hidden)
        3. Otherwise show normal content
      */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : grids.length === 0 ? (
        /* Show empty state UI regardless of error state when no grids found */
        hasActiveFilters ? (
          <EmptyState 
            title="No Tests Match Your Filters"
            message="Try adjusting your filter criteria or clear all filters to see your test history."
            actionText="Clear All Filters"
            onAction={() => handleApplyFilters(initialFilters)}
            icon={<FilterAltOffIcon sx={{ fontSize: 60, opacity: 0.7, color: 'primary.main' }} />}
          />
        ) : (
          <EmptyState 
            title={isPremium ? "Your Test History is Empty" : "Start Your Vision Monitoring Journey"}
            message={isPremium 
              ? "You haven't taken any Amsler Grid tests yet. Regular testing helps monitor changes in your vision over time and can detect early signs of macular degeneration."
              : "Track changes in your vision by taking your first Amsler Grid test. Regular testing creates a valuable baseline for monitoring your eye health."}
            actionText={isPremium ? "Take Your First Test" : "Create Your First Test"}
            actionLink="/test"
            secondaryActionText={!isPremium ? "Upgrade to Premium" : undefined}
            onSecondaryAction={!isPremium ? () => setSubscriptionModalOpen(true) : undefined}
            icon={<AutoAwesomeIcon sx={{ fontSize: 60, opacity: 0.7, color: isPremium ? 'primary.main' : '#BB86FC' }} />}
            animationDuration={0.7}
          />
        )
      ) : (
        <>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {grids.map((grid) => (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                md={4} 
                key={`grid-${grid.id}-${grid.timestamp}`}
              >
                <AmslerGridCard
                  grid={grid}
                  onView={() => handleGridOpen(grid)}
                  onDelete={() => handleDeleteGrid(grid.id)}
                  onShare={() => handleShareGrid(grid)}
                  onDownload={() => handleDownloadGrid(grid)}
                />
              </Grid>
            ))}
          </Grid>
          
          {/* Pagination */}
          <Pagination 
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            disabled={loading}
          />
        </>
      )}

      {/* Dialogs */}
      <AmslerGridDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        grid={selectedGrid}
        onShare={(grid: AmslerGridData) => handleShareGrid(grid)}
        onDownload={(grid: AmslerGridData) => handleDownloadGrid(grid)}
        onDelete={(grid: AmslerGridData) => handleDeleteGrid(grid.id)}
      />
      
      <FilterDialog 
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        onApply={handleApplyFilters}
        currentFilters={filters}
      />
      
      <SubscriptionModal
        open={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteConfirmOpen}
        onClose={cancelDeleteGrid}
        onConfirm={confirmDeleteGrid}
        itemName="Amsler Grid test"
      />

      {showRefreshDialog && (
        <Box sx={{ position: 'fixed', top: 80, left: 0, right: 0, zIndex: 2000, display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ bgcolor: '#fffbe6', border: '1px solid #ffe082', borderRadius: 2, p: 3, boxShadow: 3, maxWidth: 400 }}>
            <Typography variant="h6" color="warning.main" gutterBottom>
              Manual Refresh Needed
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please refresh the page to see new data.<br />
              (Auto-refresh occurs every {isPremium ? 5 : 10} minutes with activity.
              {!isPremium && ' Subscribe for faster updates!'})
            </Typography>
          </Box>
        </Box>
      )}
      
      {/* Add the FullTestResultDialog component */}
      <FullTestResultDialog
        open={testResultDialogOpen}
        onClose={handleCloseTestResultDialog}
        testId={selectedTestId}
      />
      
      {/* Debug information footer */}
      {process.env.NODE_ENV !== 'production' && (
        <Box sx={{ 
          mt: 4, 
          pt: 2, 
          borderTop: '1px dashed rgba(255,255,255,0.1)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          fontSize: '0.75rem',
          color: 'rgba(255,255,255,0.4)'
        }}>
          <Typography variant="caption" sx={{ mb: 1 }}>
            Debug Info (Development Only)
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box>
              <Badge 
                color={error ? "error" : "success"} 
                variant="dot"
                sx={{ mr: 1 }}
              />
              Status: {loading ? "Loading" : error ? "Error" : grids.length === 0 ? "Empty" : "Has Data"}
            </Box>
            <Box>
              Data: {grids.length} items
            </Box>
            <Box>
              Empty Check: {localStorage.getItem('lastEmptyCheck') || "Not Run"}
            </Box>
            <Button 
              variant="outlined" 
              size="small" 
              color="primary"
              onClick={refreshCurrentPage}
              sx={{ fontSize: '0.7rem', py: 0 }}
            >
              Force Refresh
            </Button>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default Dashboard; 